import { createServerFn } from "@tanstack/react-start";
import { and, count, eq, isNotNull, ne, or, sql } from "drizzle-orm";
import { z } from "zod";
import { db } from "~/lib/db";
import { importBatches, projectCitations } from "~/lib/db/schema";
import { requireProjectAccess, requireUser } from "~/lib/server/helpers.server";
import type { PipelineStage, StageStats, UserScreeningStats } from "./mock-data";

const projectIdSchema = z.object({
  projectId: z.string(),
});

interface ImportStats {
  totalImported: number;
  totalRecords: number;
  duplicatesFound: number;
  failedRecords: number;
  pendingDuplicateGroups: number;
}

async function getImportStats(projectId: string): Promise<ImportStats> {
  // Get aggregated import batch stats
  const batches = await db.query.importBatches.findMany({
    where: eq(importBatches.projectId, projectId),
  });

  let totalRecords = 0;
  let totalImported = 0;
  let duplicatesFound = 0;
  let failedRecords = 0;

  for (const batch of batches) {
    totalRecords += batch.recordsTotal ?? 0;
    totalImported += batch.recordsImported ?? 0;
    duplicatesFound += batch.recordsDuplicated ?? 0;
    failedRecords += batch.recordsFailed ?? 0;
  }

  // Get unique pending duplicate group count
  const pendingGroupsResult = await db
    .selectDistinct({ groupId: projectCitations.duplicateGroupId })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.duplicateStatus, "potential_duplicate"),
        isNotNull(projectCitations.duplicateGroupId),
      ),
    );

  return {
    totalRecords,
    totalImported,
    duplicatesFound,
    failedRecords,
    pendingDuplicateGroups: pendingGroupsResult.length,
  };
}

interface ScreeningStatsRaw {
  total: number;
  pending: number;
  included: number;
  excluded: number;
  maybe: number;
}

async function getScreeningStats(projectId: string): Promise<ScreeningStatsRaw> {
  // Get citations that are not confirmed duplicates (eligible for screening)
  const result = await db
    .select({
      screeningStatus: projectCitations.screeningStatus,
      count: count(),
    })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          sql`${projectCitations.duplicateStatus} IS NULL`,
        ),
      ),
    )
    .groupBy(projectCitations.screeningStatus);

  const stats: ScreeningStatsRaw = {
    total: 0,
    pending: 0,
    included: 0,
    excluded: 0,
    maybe: 0,
  };

  for (const row of result) {
    const statusCount = row.count;
    stats.total += statusCount;

    switch (row.screeningStatus) {
      case "pending":
        stats.pending = statusCount;
        break;
      case "included":
        stats.included = statusCount;
        break;
      case "excluded":
        stats.excluded = statusCount;
        break;
      case "maybe":
        stats.maybe = statusCount;
        break;
    }
  }

  return stats;
}

async function getUserScreeningStats(
  projectId: string,
  userId: string,
): Promise<UserScreeningStats> {
  // Count citations screened by this user
  const screenedResult = await db
    .select({ count: count() })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.screenedBy, userId),
      ),
    );

  const screened = screenedResult[0]?.count ?? 0;

  // Count total pending citations (user's remaining queue)
  const pendingResult = await db
    .select({ count: count() })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.screeningStatus, "pending"),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          sql`${projectCitations.duplicateStatus} IS NULL`,
        ),
      ),
    );

  const remaining = pendingResult[0]?.count ?? 0;

  return { screened, remaining };
}

export const $getPipelineStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }): Promise<PipelineStage[]> => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Fetch all stats in parallel
    const [importStats, screeningStats, userStats] = await Promise.all([
      getImportStats(data.projectId),
      getScreeningStats(data.projectId),
      getUserScreeningStats(data.projectId, user.id),
    ]);

    // Determine stage statuses
    const hasCitations = screeningStats.total > 0;
    const hasPendingDuplicates = importStats.pendingDuplicateGroups > 0;
    const importComplete = hasCitations && !hasPendingDuplicates;

    // For single-reviewer model, adapt the stats:
    // - "done" = included + excluded (final decisions)
    // - "pending" = pending + maybe (needs decision)
    // - No "one_vote" or "conflicts" since there's only one reviewer
    const screeningDone = screeningStats.included + screeningStats.excluded;
    const screeningPending = screeningStats.pending + screeningStats.maybe;
    const titleAbstractComplete = screeningPending === 0 && screeningStats.total > 0;

    // Build import stage stats
    const importStageStats: StageStats = {
      total: importStats.totalRecords,
      noVotes: 0,
      oneVote: 0,
      conflicts: hasPendingDuplicates ? importStats.pendingDuplicateGroups : 0,
      done: importStats.totalImported,
      excluded: importStats.duplicatesFound,
    };

    // Build title & abstract screening stats
    const titleAbstractStats: StageStats = {
      total: screeningStats.total,
      noVotes: screeningStats.pending,
      oneVote: screeningStats.maybe, // "maybe" = needs more review
      conflicts: 0, // No conflicts in single-reviewer model
      done: screeningDone,
      excluded: screeningStats.excluded,
    };

    // Full-text review - uses citations marked as "included" from T&A screening
    // For now, this stage is locked until T&A is complete
    const fullTextTotal = screeningStats.included;
    const fullTextStats: StageStats = {
      total: fullTextTotal,
      noVotes: fullTextTotal, // All pending in full-text (no separate tracking yet)
      oneVote: 0,
      conflicts: 0,
      done: 0, // No full-text screening implemented yet
      excluded: 0,
    };

    // Data extraction - uses citations that pass full-text review
    const extractionStats: StageStats = {
      total: 0,
      noVotes: 0,
      oneVote: 0,
      conflicts: 0,
      done: 0,
      excluded: 0,
    };

    const stages: PipelineStage[] = [
      {
        id: "import",
        stageNumber: 1,
        title: "Import References",
        status: importComplete ? "complete" : "active",
        stats: importStageStats,
      },
      {
        id: "title_abstract",
        stageNumber: 2,
        title: "Title & Abstract Screening",
        status: !importComplete
          ? "locked"
          : titleAbstractComplete
            ? "complete"
            : "active",
        stats: titleAbstractStats,
        userStats: importComplete ? userStats : undefined,
        unlockMessage: !importComplete
          ? hasPendingDuplicates
            ? "Resolve duplicate groups to unlock"
            : "Import citations to unlock"
          : undefined,
        unlockProgress: !importComplete
          ? hasCitations
            ? Math.round(
                ((importStats.totalImported - importStats.pendingDuplicateGroups) /
                  importStats.totalImported) *
                  100,
              )
            : 0
          : undefined,
      },
      {
        id: "full_text",
        stageNumber: 3,
        title: "Full-Text Review",
        status: !titleAbstractComplete ? "locked" : "active",
        stats: fullTextStats,
        userStats: titleAbstractComplete
          ? { screened: 0, remaining: fullTextTotal }
          : undefined,
        unlockMessage: !titleAbstractComplete
          ? "Complete Title & Abstract screening to unlock"
          : undefined,
        unlockProgress: !titleAbstractComplete
          ? screeningStats.total > 0
            ? Math.round((screeningDone / screeningStats.total) * 100)
            : 0
          : undefined,
      },
      {
        id: "extraction",
        stageNumber: 4,
        title: "Data Extraction",
        status: "locked", // Always locked for now (not implemented)
        stats: extractionStats,
        unlockMessage: "Complete Full-Text Review to unlock",
        unlockProgress: 0,
      },
    ];

    return stages;
  });
